// Map #5 — Glacier Ruins (sim half). Frozen plains around the ruins of an old stone civilisation: wind-carved snow drifts,
// ice-sheathed pillars from a broken colonnade, frost rubble, snow-laden conifers and lakes of dark clear ice. Default look:
// dawn (low pink-gold sun on snow, long blue shadows). Nothing burns here: blasts melt dark wet patches into the snow
// (fire: false keeps the shared scorch / char marks, the renderer paints them as meltwater and lets them steam).
// Hazards (owner 2026-11-02: light, always telegraphed, the same for the hero and the foes):
//   1. DARK ICE (slip): frozen lake patches, permanent, visible from the start. Everyone is slow to start, stop and turn.
//   2. PILLAR COLLAPSE (crush): a frozen pillar's ice crown shears off along a lane. Frost cracks race along the lane for
//      2.8 s first; then it lands once (the pillar breaks, foes under it are crushed, the hero takes a fixed hit).
//   3. WHITEOUT GUST (fog, global): a blizzard front rolls through every ~3 min; visibility drops to 55 % for 16 s, with a
//      3.5 s warning gust first (everyone is a little slower inside it).
// PURE: no renderer, no DOM, no unseeded randomness (check:ip enforces it). Everything random comes from ctx.rng.
import type { HazardCtx, MapCoreDef, PropSpec } from '../../core/mapdef';
import type { HazardDef } from '../../core/hazards';
import type { Prop, VariantDef } from '../../core/terrain';
import { CLASSIC_HILLS } from '../../core/terrain';

/** snow-laden conifers (r = trunk hit radius at s = 1; the renderer's shapes in props.ts match these, in order) */
export const GLACIER_TREES: VariantDef[] = [
  { name: 'fir', r: 0.45, s: [0.9, 1.25], h: [1.0, 1.18], hp: 1, w: 3 },     // tall narrow snow-laden fir
  { name: 'spruce', r: 0.62, s: [0.9, 1.2], h: [0.9, 1.05], hp: 1.25, w: 2 }, // broad heavy-boughed spruce
  { name: 'snag', r: 0.36, s: [0.8, 1.1], h: [0.9, 1.1], hp: 0.6, w: 1 },     // dead frozen snag hung with icicles
  { name: 'sapling', r: 0.3, s: [0.7, 0.95], h: [0.85, 1.05], hp: 0.5, w: 2 }, // small snowy young fir
];
/** frozen shrubs (r = the leafy / icy mass; drawn x BUSH_K) */
export const GLACIER_BUSHES: VariantDef[] = [
  { name: 'drift', r: 1.15, s: [0.8, 1.2], h: [0.85, 1.1], hp: 1, w: 3 },    // snow-buried shrub, twig tips poking out
  { name: 'juniper', r: 1.4, s: [0.8, 1.15], h: [0.85, 1.05], hp: 1.2, w: 2 }, // low creeping dark juniper with a snow crust
  { name: 'rimebriar', r: 0.8, s: [0.85, 1.2], h: [0.9, 1.15], hp: 0.8, w: 2 }, // spiky ice-crystal briar (glitters)
];

const PROPS: PropSpec[] = [
  // frozen pillars of the old colonnade (tall solid landmarks; slimmer than the canyon mesas)
  { kind: 'spire', count: 11, rMin: 26, inset: 7, size: [2.4, 3.6], clear: 10, h: [10, 17], hpPer: 3.2 },
  // ice blocks and frost-covered carved stone (solid)
  { kind: 'boulder', count: 24, rMin: 13, inset: 3, size: [1.4, 2.4], clear: 4, h: [0.8, 1.3], hpPer: 2 },
  // conifers
  { kind: 'tree', count: 38, rMin: 11, inset: 2, size: [0.5, 0.5], clear: 1.6, h: [1, 1], variants: GLACIER_TREES },
  // ice chunks and broken masonry
  { kind: 'rock', count: 46, rMin: 8, inset: 2, size: [0.5, 0.9], clear: 1, h: [0.8, 1.2] },
  // frozen shrubs, in clumps (half of them hug the conifers)
  { kind: 'bush', count: 34, rMin: 10, inset: 2, size: [1, 1], clear: 0, h: [1, 1], variants: GLACIER_BUSHES, layout: 'clumps' },
];

/** hazard tuning (exported for the tests and the renderer) */
export const GLACIER_HZ = {
  lakes: 7, lakeR: [5, 8.5] as [number, number], slip: 0.85,
  topples: 5, toppleL: 8, toppleW: 2.2, toppleWarn: 2.8, topplePower: 18, toppleFirst: 80,
  gustStart: 150, gustEvery: 170, gustWarn: 3.5, gustActive: 16, gustPower: 0.45,
  /** nothing hazardous this close to the spawn */
  spawnClear: 14,
} as const;

/** lake / topple counts were authored on the 110 m arena; they scale with the arena area (owner 2026-11-02: 160 m) */
export const glacierCounts = (arenaR: number): { lakes: number; topples: number } => {
  const k = Math.max(1, (arenaR / 110) ** 2);
  return { lakes: Math.round(GLACIER_HZ.lakes * k), topples: Math.round(GLACIER_HZ.topples * k) };
};

function hazards({ rng, arenaR, runLen, props }: HazardCtx): HazardDef[] {
  const H = GLACIER_HZ, C = glacierCounts(arenaR), out: HazardDef[] = [];
  // ---- 1. dark ice lakes: pick, for each lake, the candidate spot that sits on the fewest props (never on a solid one)
  const lakes: { x: number; z: number; r: number }[] = [];
  for (let k = 0; k < C.lakes; k++) {
    let best: { x: number; z: number; r: number } | null = null, bestS = Infinity;
    for (let t = 0; t < 16; t++) {
      const r = rng.range(H.lakeR[0], H.lakeR[1]), a = rng.range(0, Math.PI * 2);
      const d = Math.sqrt(rng.range(((22 + r) / arenaR) ** 2, ((arenaR - 12 - r) / arenaR) ** 2)) * arenaR;
      const x = Math.cos(a) * d, z = Math.sin(a) * d;
      let s = 0;
      for (const p of props) {
        const q = Math.hypot(p.x - x, p.z - z);
        if (q < r + p.r + 0.5) s += p.solid ? 12 : 1;
      }
      for (const l of lakes) if (Math.hypot(l.x - x, l.z - z) < l.r + r + 10) s += 100;
      if (s < bestS) { bestS = s; best = { x, z, r }; }
    }
    lakes.push(best!);
    out.push({ kind: 'slip', x: best!.x, z: best!.z, r: best!.r, start: 0, warn: 0.6, active: Infinity, power: H.slip, tag: 'ice' });
  }
  // ---- 2. pillar collapse: a few frozen pillars (spires) shed their ice crown along a lane, one after another
  const spires = props.filter((p) => p.kind === 'spire' && Math.hypot(p.x, p.z) > 28);
  for (let i = spires.length - 1; i > 0; i--) { const j = rng.int(i + 1); [spires[i], spires[j]] = [spires[j], spires[i]]; } // seeded shuffle
  const picks = spires.slice(0, C.topples), last = Math.max(runLen * 0.85, H.toppleFirst + 60 * picks.length);
  picks.forEach((s: Prop, k) => {
    // fall roughly tangential (across the arena, never at the spawn); flip if the lane would reach the spawn clearing
    const base = Math.atan2(s.z, s.x);
    let rot = base + (rng.next() < 0.5 ? 1 : -1) * (Math.PI / 2 + rng.range(-0.55, 0.55));
    const lane = (ang: number) => { const c = Math.cos(ang), n = Math.sin(ang), off = H.toppleL - s.r * 0.5; return { x: s.x + c * off, z: s.z + n * off, ex: s.x + c * (off + H.toppleL), ez: s.z + n * (off + H.toppleL) }; };
    let L = lane(rot);
    if (Math.min(Math.hypot(L.x, L.z), Math.hypot(L.ex, L.ez)) < H.spawnClear + H.toppleW) { rot += Math.PI; L = lane(rot); }
    const start = H.toppleFirst + ((last - H.toppleFirst) * k) / Math.max(1, picks.length) + rng.range(-8, 8);
    out.push({ kind: 'crush', x: L.x, z: L.z, r: 0, l: H.toppleL, w: H.toppleW, rot, start, warn: H.toppleWarn, active: 0, power: H.topplePower, tag: 'topple' });
  });
  // ---- 3. whiteout gust: a blizzard front over the whole arena, now and then
  out.push({ kind: 'fog', x: 0, z: 0, r: 0, global: true, start: Math.min(H.gustStart, runLen * 0.45), warn: H.gustWarn, active: H.gustActive, every: H.gustEvery, power: H.gustPower, tag: 'blizzard' });
  return out;
}

export const MAP: MapCoreDef = {
  id: 'glacier-ruins',
  name: 'Glacier Ruins',
  tagline: 'Frozen plains around a fallen colonnade at dawn. Dark ice is slick for everyone, the old pillars still fall and blizzards blot out the view.',
  order: 5,
  status: 'ready',
  time: { default: 'day', day: 'dawn' },
  unlock: { after: 'verdant-alien', seconds: 600 },
  // wind-carved snow drifts (more, softer mounds) and a few shallow frozen hollows
  terrain: { hills: { ...CLASSIC_HILLS, mounds: 16, dip: 0.3, rad: [12, 26], amp: [0.6, 2.2], dipAmp: [0.4, 0.9], swell: 0.55, max: 2.8 }, props: PROPS },
  fire: false,
  nightPressure: 1.15,
  boss: { name: 'HOARSTONE KEEPER', hpMul: 1.15 },
  hazards,
};
